import { useCallback, useRef, useState } from 'react';

import { humanizeSaveError } from '@/lib/crm/utils/humanizeSaveError';

export const NOOP_SAVE = async (): Promise<void> => {};

/**
 * Outcome of a `commit` call:
 * - `'saved'`  — the save resolved and the editor closed.
 * - `'ignored'` — this commit did nothing to the current edit and the caller
 *   must not roll back the draft. Two cases produce it: (a) a save for this
 *   edit is already in flight, so this duplicate is deduped (e.g. the title
 *   editor fires commit on both Enter and the blur that Enter causes); and
 *   (b) a stale completion — a save that resolves/rejects after the editor was
 *   cancelled and reopened, which no longer owns the current edit.
 * - `'failed'` — the save rejected for the CURRENT edit; the editor stays open
 *   showing the error.
 */
export type CommitOutcome = 'saved' | 'ignored' | 'failed';

export function useInlineFieldEdit<T>(onSave: (value: T) => Promise<void>): {
  isEditing: boolean;
  isSaving: boolean;
  error: string | null;
  startEditing: () => void;
  cancelEditing: () => void;
  commit: (value: T) => Promise<CommitOutcome>;
} {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // `pendingRef` serialises saves across a whole edit session: it is set when
  // an `onSave` starts and cleared ONLY when that same `onSave` settles — NOT
  // on cancel/reopen. So while a save's request is still running, a fresh
  // commit (even after the user cancelled and reopened the field) is refused
  // rather than started, guaranteeing the two `onSave` requests can never race
  // each other and persist out of order. `generationRef` fences every
  // completion to the edit that started it: a save that settles after the
  // editor was cancelled/reopened (a stale completion) is bumped out of the
  // current generation and applies no state. `onSave` here is not abortable,
  // so serialising is how the old request is prevented from clobbering the new
  // value — see the P1 finding on this file.
  const pendingRef = useRef(false);
  const generationRef = useRef(0);

  const startEditing = useCallback(() => {
    generationRef.current += 1;
    setIsEditing(true);
    setError(null);
  }, []);

  const cancelEditing = useCallback(() => {
    generationRef.current += 1;
    setIsEditing(false);
    setError(null);
  }, []);

  const commit = useCallback(
    async (value: T): Promise<CommitOutcome> => {
      // A previous save is still settling — refuse a new one until it resolves.
      // Because `pendingRef` is not cleared by cancel/reopen, this also blocks
      // the cancel→reopen→commit sequence that would otherwise launch a second
      // overlapping `onSave`. Report it as `'ignored'` (not `'failed'`) so the
      // caller doesn't mistake a deduped/serialised commit for a rejection and
      // roll the draft back.
      if (pendingRef.current) {
        return 'ignored';
      }
      pendingRef.current = true;
      setIsSaving(true);
      const generation = generationRef.current;
      setError(null);
      try {
        await onSave(value);
        // Stale completion: the editor was cancelled and reopened while this
        // save was in flight, so it no longer owns the current edit. Apply no
        // state and report `'ignored'` (not `'saved'`) so the caller leaves the
        // freshly reopened draft alone.
        if (generation !== generationRef.current) {
          return 'ignored';
        }
        setIsEditing(false);
        return 'saved';
      } catch (e) {
        // A stale rejection must not be reported as `'failed'`: the caller
        // rolls the draft back to the server value on `'failed'`, which would
        // wipe the user's in-progress edit in the reopened editor. Report
        // `'ignored'` so the current edit is untouched.
        if (generation !== generationRef.current) {
          return 'ignored';
        }
        setError(humanizeSaveError(e instanceof Error ? e.message : String(e)));
        return 'failed';
      } finally {
        // Always clear the in-flight guard when THIS save settles, regardless
        // of generation — the request is done, so the next commit may proceed.
        pendingRef.current = false;
        setIsSaving(false);
      }
    },
    [onSave]
  );

  return { isEditing, isSaving, error, startEditing, cancelEditing, commit };
}
